import { useCallback, useContext, useMemo } from 'react';
import { FormikHelpers } from 'formik';
import { message } from 'antd';

import { BranchesService } from '../../services';
import { BranchFormMapper } from '../../mappers';
import { IBranchFormValues } from '../../interfaces';
import { CreateBranchContext } from '../../context';
import { useBranchById } from './use-branch-by-id';

const emptyValues: IBranchFormValues = {
  id: '',
  name: '',
  descr: '',
  address: '',
  phone: '',
  email: '',
  workinghours: '',
  isBranch: false,
  isRegionBranch: false,
  hide: false,
  latitude: '',
  longitude: '',
  mapUrl: '',
  mapAddress: '',
  parentId: '',
  stateId: '',
  companyId: '',
  sortingLetter: '',
  openHour: '',
  closeHour: '',
  openHourSaturday: '',
  closeHourSaturday: '',
  cityName: '',
  provinceName: '',
  postCode: '',
  warehouseMan: '',
};

/** Column-length limits the API enforces; checked client-side so the user sees them per-field. */
const MAX_COORDINATE_LENGTH = 15;
const MAX_SORTING_LETTER_LENGTH = 5;

export const useBranchForm = () => {
  const { id, onSucceed } = useContext(CreateBranchContext);
  const branch = useBranchById(id, { enabled: !!id });

  const initialValues = useMemo<IBranchFormValues | undefined>(() => {
    if (id) {
      return branch.data ? BranchFormMapper.toDomain(branch.data) : undefined;
    }
    return emptyValues;
  }, [id, branch.data]);

  const onSubmit = useCallback(
    async (values: IBranchFormValues, helpers: FormikHelpers<IBranchFormValues>) => {
      helpers.setStatus(null);

      let key: keyof IBranchFormValues | null = null;

      if (values.longitude?.length > MAX_COORDINATE_LENGTH) {
        key = 'longitude';
      } else if (values.latitude?.length > MAX_COORDINATE_LENGTH) {
        key = 'latitude';
      } else if (values.sortingLetter?.length > MAX_SORTING_LETTER_LENGTH) {
        key = 'sortingLetter';
      }

      if (key) {
        const characterCount = key === 'sortingLetter' ? MAX_SORTING_LETTER_LENGTH : MAX_COORDINATE_LENGTH;
        helpers.setErrors({ [key]: `Maksimum ${characterCount} simvol...` });
        helpers.setSubmitting(false);
        return;
      }

      const result = await BranchesService.save(values, id || undefined);

      if (result.status === 200) {
        message.success(id ? 'Dəyişikliklər saxlanıldı' : 'Filial yaradıldı');
        onSucceed();
      } else if (result.status === 422) {
        helpers.setErrors(result.data);
      } else {
        message.error(result.data as string);
      }

      helpers.setSubmitting(false);
    },
    [id, onSucceed],
  );

  return { initialValues, onSubmit, id, isLoading: !!id && branch.isLoading };
};
